// CTSK-316 — Upload-here affordance for tool registry pages.
//
// The tool native pages (DSR + Timeline Lane) render registered
// registry files out of the tool's bound container, but landing a
// fresh file meant a detour through the Documents UI plus a manual
// container move — the papercut CTSK-303's smoke run surfaced. This
// button opens a small, self-contained upload dialog that:
//
//   - preselects the tool's bound container (registry_container_id)
//     as the destination — the operator can't misroute the file
//   - restricts accepted filenames to the tool's registered
//     registryFiles[].path list (the only names the registry pointer
//     resolves; anything else would land invisible to the tabs)
//   - detects overwrites via the SAME matcher the tabs use
//     (GET /tools/:id/registry?path=): when the selected path already
//     resolves to a document, submit becomes a two-step
//     "Replace existing?" confirm. On confirm the old document is
//     soft-deleted first, so the pointer resolves the NEW file —
//     CTSK-258 excludes soft-deleted rows from the match, which is
//     what makes replace-by-delete safe here.
//   - uploads through the standard POST /documents/upload, then binds
//     the doc into the container via PUT /documents/:id/container
//   - on success: invalidates the page's registry queries and toasts
//     "File landed. Reload to see it in the tab."
//
// Note on scope (CTSK-316 judgment call): there is no shared upload
// *dialog* component to extend — uploads today are the bulk queue on
// the Documents page and the drop-zone on container detail, both
// dialogless. So this component owns its dialog outright instead of
// refactoring org-level upload state (the escalation note anticipated
// exactly that rat-hole).

import { useMemo, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { RegistryFileEntry } from '@captain-tasko/shared';
import { apiFetch, apiSingle, apiUpload } from '../lib/api.js';
import { toast } from './Toast.js';

interface RegistryPointer {
  toolId: string;
  registryPath: string;
  documentId?: string;
}

export interface ToolRegistryUploadButtonProps {
  toolId: string;
  /** The tool's bound container (registry_container_id) — the upload
   *  destination is preselected to this and not operator-changeable. */
  containerId: string;
  /** Human-readable container name/slug for the dialog copy. */
  containerLabel?: string | null;
  /** The tool's registered registry files — the restricted filename
   *  list. An empty list renders nothing (nothing could be resolved). */
  registryFiles: RegistryFileEntry[];
  /** The page's active tab path — preselected in the dialog. */
  activePath?: string;
  /** Fired after a successful upload + container bind. */
  onUploaded?: (documentId: string, path: string) => void;
}

export function ToolRegistryUploadButton({
  toolId,
  containerId,
  containerLabel,
  registryFiles,
  activePath,
  onUploaded,
}: ToolRegistryUploadButtonProps) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [replaceTargetId, setReplaceTargetId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const paths = useMemo(() => registryFiles.map((f) => f.path), [registryFiles]);
  const effectivePath =
    selectedPath ?? (activePath && paths.includes(activePath) ? activePath : paths[0] ?? null);

  if (registryFiles.length === 0) return null;

  // The restricted-filenames rule: the chosen file must be NAMED as the
  // selected registered path, or the registry pointer will never find
  // it. A file named as a DIFFERENT registered path auto-switches the
  // selection instead of erroring — the filename is the stronger signal.
  const filenameMismatch = file !== null && effectivePath !== null && file.name !== effectivePath;

  const reset = () => {
    setFile(null);
    setReplaceTargetId(null);
    setBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const close = () => {
    setOpen(false);
    setSelectedPath(null);
    reset();
  };

  const handleFileChange = (files: FileList | null) => {
    const f = files?.[0] ?? null;
    setReplaceTargetId(null);
    if (f && paths.includes(f.name) && f.name !== effectivePath) {
      // Auto-switch the target to match the file's own (registered) name.
      setSelectedPath(f.name);
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file || !effectivePath || filenameMismatch || busy) return;
    setBusy(true);
    try {
      // 1. Overwrite detection — same matcher the tab uses.
      if (replaceTargetId === null) {
        const ptr = await apiSingle<RegistryPointer>(`/tools/${toolId}/registry`, {
          query: { path: effectivePath },
        });
        if (ptr.documentId) {
          // Two-step confirm: surface the prompt, require a second
          // explicit submit before anything is touched.
          setReplaceTargetId(ptr.documentId);
          setBusy(false);
          return;
        }
      } else {
        // Confirmed replace: soft-delete the old doc so the registry
        // pointer resolves the new upload (CTSK-258 filters deleted).
        await apiFetch<void>(`/documents/${replaceTargetId}`, { method: 'DELETE' });
      }

      // 2. Standard upload, then bind into the preselected container.
      const res = await apiUpload<{ document: { id: string } }>('/documents/upload', file);
      const documentId = res.document.id;
      await apiSingle<{ ok: boolean }>(`/documents/${documentId}/container`, {
        method: 'PUT',
        body: { containerId },
      });

      // 3. Freshen the page — pointer + content queries both re-run.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['tool-registry'] }),
        qc.invalidateQueries({ queryKey: ['tool-registry-content'] }),
        qc.invalidateQueries({ queryKey: ['documents'] }),
      ]);
      toast.success('File landed. Reload to see it in the tab.');
      onUploaded?.(documentId, effectivePath);
      close();
    } catch (err) {
      toast.fromError(err, `Failed to upload ${effectivePath}`);
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen(true)}
        title="Upload a registry file into this tool's bound container"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <Upload size={14} aria-hidden /> Upload registry file
      </button>

      {open && (
        <div
          role="presentation"
          onClick={close}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Upload registry file"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 460, maxWidth: '92vw', background: 'var(--surface-raised)',
              border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)',
              padding: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
            }}
          >
            <h3 style={{ margin: '0 0 4px 0', fontSize: 16 }}>Upload registry file</h3>
            <p style={{ margin: '0 0 14px 0', fontSize: 12, color: 'var(--text-muted)' }}>
              Lands in the tool's bound container{' '}
              <code style={{ fontFamily: 'var(--font-mono)' }}>{containerLabel ?? containerId}</code>{' '}
              — no container picking, no post-upload move.
            </p>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Registered file
              <select
                value={effectivePath ?? ''}
                onChange={(e) => { setSelectedPath(e.target.value); setReplaceTargetId(null); }}
                style={{
                  display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', fontSize: 13,
                  border: '1px solid var(--border-strong)', borderRadius: 4, background: 'var(--surface-raised)',
                }}
              >
                {registryFiles.map((f) => (
                  <option key={f.path} value={f.path}>{f.label} — {f.path}</option>
                ))}
              </select>
            </label>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '12px 0 4px' }}>
              File
              <input
                ref={fileInputRef}
                type="file"
                aria-label="Registry file to upload"
                onChange={(e) => handleFileChange(e.target.files)}
                style={{ display: 'block', marginTop: 4, fontSize: 13 }}
              />
            </label>

            {filenameMismatch && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger-primary, #B3261E)' }}>
                <strong>{file!.name}</strong> isn't a registered registry filename for this tool.
                Rename it to{' '}
                <code style={{ fontFamily: 'var(--font-mono)' }}>{effectivePath}</code>{' '}
                (or pick the matching registered file) — the registry tabs only resolve
                registered names.
              </div>
            )}

            {replaceTargetId !== null && (
              <div
                style={{
                  marginTop: 10, padding: '8px 10px', fontSize: 12, borderRadius: 4,
                  background: 'color-mix(in srgb, var(--warning-primary, #B86E4D) 12%, var(--surface-sunken))',
                }}
              >
                <strong>Replace existing?</strong> A document for{' '}
                <code style={{ fontFamily: 'var(--font-mono)' }}>{effectivePath}</code>{' '}
                already exists in this container. Uploading will replace it (the old copy is
                soft-deleted and stays recoverable from the Documents trash).
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn btn--ghost" onClick={close} disabled={busy}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => { void submit(); }}
                disabled={!file || filenameMismatch || busy}
              >
                {busy ? 'Uploading…' : replaceTargetId !== null ? 'Replace + upload' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
